import { useState } from 'react'
import {
  aliasRejectionMessage,
  suggestAliases,
  validateAlias,
  type NamedCodeLike,
} from '@budgetmonkey/shared'
import { Button, Input } from '@/components/ui'

/**
 * Alias editing — REQUIREMENTS.md §7.6.
 *
 * Aliases are how lookup works, so ambiguity here makes lookup ambiguous. Two
 * consequences visible in this component:
 *
 *   - Duplicates are refused at entry with the conflicting code named, rather
 *     than resolved with a "did you mean?" prompt on every future lookup.
 *   - Suggestions only ever offer *distinctive* code segments. All ten of the
 *     main-fund codes carry `329` in segment 8, so suggesting it would produce
 *     an alias that matches everything and finds nothing.
 */
export function AliasEditor({
  code,
  aliases,
  onChange,
  existingCodes,
  currentCodeId,
}: {
  /** The account code, used to suggest distinctive segments. */
  code: string
  aliases: string[]
  onChange: (next: string[]) => void
  /** Every other code in this user + fiscal year, for uniqueness checking. */
  existingCodes: readonly NamedCodeLike[]
  currentCodeId?: string | undefined
}) {
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)

  const suggestions = suggestAliases(code, existingCodes).filter(
    (s) => !aliases.includes(s),
  )

  function add(value: string) {
    const candidate = value.trim().toLowerCase()
    if (candidate === '') return

    if (aliases.includes(candidate)) {
      setError('That alias is already on this code.')
      return
    }
    const rejection = validateAlias(candidate, existingCodes, {
      excludeCodeId: currentCodeId,
    })
    if (rejection !== null) {
      setError(aliasRejectionMessage(rejection))
      return
    }

    onChange([...aliases, candidate])
    setDraft('')
    setError(null)
  }

  return (
    <div className="space-y-2">
      <span className="block text-sm font-medium text-slate-800">Aliases</span>

      {aliases.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {aliases.map((alias) => (
            <li key={alias}>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 py-1 pl-3 pr-1.5 text-sm">
                {alias}
                <button
                  type="button"
                  onClick={() => onChange(aliases.filter((a) => a !== alias))}
                  aria-label={`Remove alias ${alias}`}
                  className="flex size-6 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-300 hover:text-slate-900"
                >
                  ×
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value)
            setError(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // This editor is used inside forms; Enter adds an alias rather
              // than submitting the whole form.
              e.preventDefault()
              add(draft)
            }
          }}
          placeholder="Type an alias, press Enter"
          invalid={error !== null}
          aria-label="New alias"
        />
        <Button type="button" onClick={() => add(draft)} disabled={draft.trim() === ''}>
          Add
        </Button>
      </div>

      {error !== null ? (
        <p role="alert" className="text-xs font-medium text-red-700">
          {error}
        </p>
      ) : (
        <p className="text-xs text-slate-500">
          Shortcuts you'll type to find this code — letters, numbers, and hyphens.
        </p>
      )}

      {suggestions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="text-xs text-slate-500">Suggested:</span>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => add(suggestion)}
              className="rounded-full border border-dashed border-slate-300 px-3 py-1 text-sm text-slate-600 transition hover:border-slate-900 hover:text-slate-900"
            >
              + {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
